import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { nodes, links } from './Data';


// ─── Helper: derive readable metadata from a node ────────────────────────────
function getNodeMeta(node, allLinks) {
  const outbound = allLinks.filter(l => (l.source?.id ?? l.source) === node.id);
  const inbound  = allLinks.filter(l => (l.target?.id ?? l.target) === node.id);
  return { outbound, inbound };
}

// ─── Ticker component ─────────────────────────────────────────────────────────
const Ticker = ({ value, className }) => {
  const [key, setKey] = useState(0);
  useEffect(() => { setKey(k => k + 1); }, [value]);
  return <span key={key} className={`tick-anim ${className ?? ''}`}>{value}</span>;
};

// ─── Main component ───────────────────────────────────────────────────────────
const NetworkGraph = () => {
  const fgRef  = useRef();
  const [searchText,    setSearchText]    = useState('');
  const [suggestions,   setSuggestions]   = useState([]);
  const [selectedNode,  setSelectedNode]  = useState(null);
  const [panelOpen,     setPanelOpen]     = useState(false);

  const data = useMemo(() => ({ nodes, links }), []);

  // Physics setup
  useEffect(() => {
    if (!fgRef.current) return;
    fgRef.current.d3Force('charge').strength(-800);
    fgRef.current.d3Force('link').distance(200);
  }, []);

  // Search
  const handleSearch = (val) => {
    setSearchText(val);
    setSuggestions(val ? data.nodes.filter(n =>
      n.name.toLowerCase().includes(val.toLowerCase())
    ) : []);
  };

  const centerOnNode = useCallback((node) => {
    fgRef.current.centerAt(node.x, node.y, 800);
    fgRef.current.zoom(3, 800);
    setSuggestions([]);
    setSearchText(node.name);
    setSelectedNode(node);
    setPanelOpen(true);
  }, []);

  const handleNodeClick = useCallback((node) => {
    fgRef.current.centerAt(node.x, node.y, 600);
    fgRef.current.zoom(3, 600);
    setSelectedNode(node);
    setPanelOpen(true);
    setSuggestions([]);
    setSearchText('');
  }, []);

  const closePanel = () => {
    setPanelOpen(false);
    setTimeout(() => setSelectedNode(null), 450);
  };

  // Derived connection data for selected node
  const nodeMeta = useMemo(() => {
    if (!selectedNode) return null;
    return getNodeMeta(selectedNode, data.links);
  }, [selectedNode, data.links]);

  // Canvas node renderer — with selected-node pulse ring
  const nodeCanvasObject = useCallback((node, ctx, globalScale) => {
    const isSelected = selectedNode?.id === node.id;
    const r = Math.sqrt(node.size ?? 16) * 2.5;

    // Pulse ring for selected
    if (isSelected) {
      const time = Date.now() / 800;
      const pulseR = r + 6 + Math.sin(time) * 3;
      ctx.beginPath();
      ctx.arc(node.x, node.y, pulseR, 0, 2 * Math.PI);
      ctx.strokeStyle = 'rgba(0,245,255,0.4)';
      ctx.lineWidth = 1.5 / globalScale;
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(node.x, node.y, pulseR + 6, 0, 2 * Math.PI);
      ctx.strokeStyle = 'rgba(0,245,255,0.12)';
      ctx.lineWidth = 1 / globalScale;
      ctx.stroke();
    }

    // Glow
    ctx.shadowColor = isSelected ? '#00f5ff' : node.color;
    ctx.shadowBlur  = isSelected ? 24 : 15;

    // Node circle
    ctx.beginPath();
    ctx.arc(node.x, node.y, r, 0, 2 * Math.PI);
    ctx.fillStyle = isSelected ? '#00f5ff' : node.color;
    ctx.fill();

    ctx.shadowBlur = 0;

    // Label
    if (globalScale > 0.3) {
      const fontSize = (node.type === 'parent' ? 18 : 12) / globalScale;
      ctx.font = `${fontSize}px 'Share Tech Mono', monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillStyle = isSelected ? '#fff' : '#b8d4e8';
      ctx.fillText(node.name, node.x, node.y + r + 2);
    }
  }, [selectedNode]);

  // Force re-render each frame so pulse ring animates
  const onRenderFramePost = useCallback(() => {
    if (selectedNode && fgRef.current) {
      fgRef.current.refresh?.();
    }
  }, [selectedNode]);

  const totalConnections = useMemo(() => data.links.length, [data.links]);

  return (
    <>
      <div className="hud-root">
        {/* Overlays */}
        <div className="grid-overlay" />
        <div className="scanlines"  />

        {/* Corner brackets */}
        <div className="corner tl" />
        <div className="corner tr" />
        <div className="corner bl" />
        <div className="corner br" />

        {/* Header */}
        <div className="hud-header">
          <div className="title">WhereDidTheyExit</div>
          <div className="sub">NETWORK TOPOLOGY v2.4 <span>By Franklin Barto</span></div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="live-label">LIVE</div>
            <div className="live-dot" />
          </div>
        </div>

        {/* Search */}
        <div className="search-wrap">
          <div className="search-label"> NODE SEARCH</div>
          <div className="search-box">
            <span className="search-icon">⌕</span>
            <input
              className="search-input"
              placeholder="QUERY ENTITY..."
              value={searchText}
              onChange={e => handleSearch(e.target.value)}
            />
          </div>
          {suggestions.length > 0 && (
            <div className="suggestions">
              {suggestions.slice(0, 5).map(s => (
                <div
                  key={s.id}
                  className="suggestion-item"
                  onClick={() => centerOnNode(s)}
                >
                  <div className="dot" style={{ background: s.color || '#00f5ff' }} />
                  {s.name}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Graph */}
        <ForceGraph2D
          ref={fgRef}
          graphData={data}
          nodeRelSize={6}
          nodeLabel={node => `${node.name}`}
          nodeCanvasObject={nodeCanvasObject}
          onNodeClick={handleNodeClick}
          linkColor={() => 'rgba(0,245,255,0.12)'}
          linkDirectionalArrowLength={3.5}
          linkDirectionalArrowRelPos={1}
          linkWidth={0.6}
          backgroundColor="#060e1f"
          onRenderFramePost={onRenderFramePost}
        />

        {/* ── Detail panel ─────────────────────────────────────── */}
        <div className={`detail-panel ${panelOpen ? 'open' : ''}`}>
          <div className="shimmer-line" />

          {selectedNode && (
            <>
              <div className="color-bar" style={{ background: selectedNode.color || '#00f5ff' }} />
              <div className="detail-header">
                <div>
                  <div className="node-name">
                    <Ticker value={selectedNode.name} />
                  </div>
                  <div className="node-type">
                    {(selectedNode.type ?? 'ENTITY').toUpperCase()}
                  </div>
                </div>
                <button className="close-btn" onClick={closePanel}>✕</button>
              </div>

              <div className="detail-body">

                {/* Core info */}
                <div className="detail-section">
                  <div className="section-label">NODE DATA</div>
                  <div className="data-row">
                    <span className="data-key">IDENTIFIER</span>
                    <span className="data-val highlight">
                      <Ticker value={selectedNode.id} />
                    </span>
                  </div>
                  {selectedNode.rel && (
                    <div className="data-row">
                      <span className="data-key">ROLE</span>
                      <span className="data-val">
                        <Ticker value={selectedNode.rel} />
                      </span>
                    </div>
                  )}
                  {selectedNode.val !== undefined && (
                    <div className="data-row">
                      <span className="data-key">WEIGHT</span>
                      <span className="data-val highlight">
                        <Ticker value={selectedNode.val ?? selectedNode.size ?? '—'} />
                      </span>
                    </div>
                  )}
                  {selectedNode.founded && (
                    <div className="data-row">
                      <span className="data-key">FOUNDED</span>
                      <span className="data-val">{selectedNode.founded}</span>
                    </div>
                  )}
                  {selectedNode.industry && (
                    <div className="data-row">
                      <span className="data-key">SECTOR</span>
                      <span className="data-val">{selectedNode.industry}</span>
                    </div>
                  )}
                  {selectedNode.employees && (
                    <div className="data-row">
                      <span className="data-key">EMPLOYEES</span>
                      <span className="data-val magenta">{selectedNode.employees}</span>
                    </div>
                  )}
                  {selectedNode.revenue && (
                    <div className="data-row">
                      <span className="data-key">REVENUE</span>
                      <span className="data-val magenta">{selectedNode.revenue}</span>
                    </div>
                  )}
                  {selectedNode.description && (
                    <div className="data-row" style={{ flexDirection: 'column', gap: 4 }}>
                      <span className="data-key">DESCRIPTION</span>
                      <span className="data-val" style={{ textAlign: 'left', opacity: 0.8, fontSize: 10 }}>
                        {selectedNode.description}
                      </span>
                    </div>
                  )}
                </div>

                {/* Connections */}
                <div className="detail-section">
                  <div className="section-label">
                    CONNECTIONS
                    <span style={{ color: 'var(--text)', marginLeft: 4 }}>
                      [{(nodeMeta?.inbound?.length ?? 0) + (nodeMeta?.outbound?.length ?? 0)}]
                    </span>
                  </div>
                  <div className="data-row">
                    <span className="data-key">OUTBOUND</span>
                    <span className="data-val highlight">{nodeMeta?.outbound?.length ?? 0}</span>
                  </div>
                  <div className="data-row">
                    <span className="data-key">INBOUND</span>
                    <span className="data-val highlight">{nodeMeta?.inbound?.length ?? 0}</span>
                  </div>
                </div>

                {/* Connected nodes list */}
                {(nodeMeta?.outbound?.length > 0 || nodeMeta?.inbound?.length > 0) && (
                  <div className="detail-section">
                    <div className="section-label">LINKED ENTITIES</div>
                    <div className="conn-list">
                      {[
                        ...nodeMeta.outbound.slice(0, 4).map(l => ({
                          id:   l.target?.id ?? l.target,
                          name: l.target?.name ?? (l.target?.id ?? l.target),
                          color:l.target?.color,
                          dir:  'OUT →',
                        })),
                        ...nodeMeta.inbound.slice(0, 4).map(l => ({
                          id:   l.source?.id ?? l.source,
                          name: l.source?.name ?? (l.source?.id ?? l.source),
                          color:l.source?.color,
                          dir:  '← IN',
                        })),
                      ].map((c, i) => (
                        <div className="conn-item" key={`${c.id}-${i}`}>
                          <div className="conn-dot" style={{ background: c.color || '#00f5ff' }} />
                          <span style={{ flex: 1 }}>{c.name || c.id}</span>
                          <span className="conn-dir">{c.dir}</span>
                        </div>
                      ))}
                      {(nodeMeta.outbound.length + nodeMeta.inbound.length) > 8 && (
                        <div style={{ fontSize: 9, color: 'var(--dim)', padding: '4px 10px', letterSpacing: '0.1em' }}>
                          + {(nodeMeta.outbound.length + nodeMeta.inbound.length) - 8} MORE ENTITIES
                        </div>
                      )}
                    </div>
                  </div>
                )}

              </div>
            </>
          )}
        </div>

        {/* Stats bar */}
        <div className="stat-bar">
          <div className="stat-chip">
            <span className="val">{data.nodes.length}</span>
            <span className="key">NODES</span>
          </div>
          <div className="stat-chip">
            <span className="val">{totalConnections}</span>
            <span className="key">EDGES</span>
          </div>
          {selectedNode && (
            <div className="stat-chip">
              <span className="val" style={{ color: 'var(--magenta)', textShadow: '0 0 10px var(--magenta)' }}>
                {(nodeMeta?.inbound?.length ?? 0) + (nodeMeta?.outbound?.length ?? 0)}
              </span>
              <span className="key">ACTIVE NODE</span>
            </div>
          )}
        </div>

        {/* Zoom controls */}
        <div className="zoom-controls">
          <button className="zoom-btn" onClick={() => fgRef.current?.zoom(fgRef.current.zoom() * 1.4, 300)}>+</button>
          <button className="zoom-btn" onClick={() => fgRef.current?.zoom(fgRef.current.zoom() / 1.4, 300)}>−</button>
        </div>

      </div>
    </>
  );
};

export default NetworkGraph;